import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import {
    COMMENTS_STATUS_OPTIONS,
    useAuth,
    useBoolean,
    useOrganization,
    useOrgFeatureFlags,
    useQuery
} from 'hooks'
import { inScreenTenantId } from 'env'

import { PlaybookType, VideoRefType } from 'app/types'
import { isQG } from 'modules'

import { CommentsButton } from './CommentsButton'
import { CommentsTimeline } from './CommentsTimeline'

export const DEFAULT_STEP_DURATION = 3

type Props = {
    videoRef: VideoRefType
    playbook: PlaybookType
}

export const Comments = ({ videoRef, playbook }: Props) => {
    const { enableComments } = useOrgFeatureFlags(playbook.uploadedByOrgId)

    const { comments, id } = useOrganization() //org value

    const { commentsStatus } = useSelector(state => state.qgMetadata)

    const { commentsStatus: playbookCommentsStatus } = playbook

    const [selectedStepId, setSelectedStepId] = useState('')

    const [relativeTime, setRelativeTime] = useState(0)

    const { uid } = useAuth()

    const isVisible = useBoolean(true)

    const isPlaybookQG = isQG(playbook)

    useEffect(() => {
        const updateSelectedStepAndRelativeTime = () => {
            const currentTime = videoRef?.current?.currentTime || 0

            let start = 0
            let relativeTime = 0

            playbook?.steps?.some(step => {
                const end = start + (step.duration || DEFAULT_STEP_DURATION)

                if (currentTime >= start && currentTime <= end) {
                    relativeTime = Math.floor((currentTime - start) * 1000)
                    setRelativeTime(relativeTime)
                    setSelectedStepId(step.id)
                    return true
                }
                start = end
                return false
            })
        }

        const currentVideoRef = videoRef.current
        currentVideoRef?.addEventListener('timeupdate', updateSelectedStepAndRelativeTime)

        return () => {
            currentVideoRef?.removeEventListener('timeupdate', updateSelectedStepAndRelativeTime)
        }
    }, [videoRef, playbook?.steps])

    const isOrgHideComment = useMemo(() => {
        const status = commentsStatus || playbookCommentsStatus || comments

        if (status === 'anyoneInOrg') {
            if (playbook.uploadedByOrgId !== id) {
                return true
            }
        }
        return false
    }, [id, playbook.uploadedByOrgId, comments, commentsStatus, playbookCommentsStatus])

    // Fetch the comment token
    const { data, isValidating } = useQuery<{ token: string | null }>(
        `/c/v1/get-inscreen-token`,
        {
            method: 'POST',
            body: { playbookId: playbook.id }
        },
        {
            onSuccess: async data => {
                if (data?.token) {
                    try {
                        await window.inScreen.initialize({
                            endpoint: 'https://eu.inscreen.com/graphql',
                            tenantId: inScreenTenantId,
                            token: data?.token,
                            testConnectionStrategy: 'retry'
                        })
                    } catch (e) {
                        console.error(e)
                    }
                }
            }
        }
    )

    // in case there is no token - don't display comments on the timeline
    if (isValidating || !data) return null

    // or feature flag is off - (enableComments)
    // or the visibility switch was turned off - (!visibility ) - NOT IMPLEMENTED YET
    // or comments is turned off in the account level - (comments === 'off')
    // or comments is turned off in the playbook level - (disableComments)
    // or account setting is restricted (anyoneInOrg) and user and org id does not match - (isOrgHideComment)
    // don't display comments on the timeline

    if (
        !enableComments ||
        comments === COMMENTS_STATUS_OPTIONS.off ||
        commentsStatus === COMMENTS_STATUS_OPTIONS.off ||
        isOrgHideComment
    )
        return null

    return (
        <>
            <CommentsTimeline
                videoElement={videoRef?.current}
                isVisible={isVisible.isTrue}
                playbook={playbook}
                uid={uid}
                selectedStepId={selectedStepId}
                relativeTime={relativeTime}
                isPlaybookQG={isPlaybookQG}
            />
            <CommentsButton
                videoElement={videoRef?.current}
                playbookId={playbook.id}
                onChange={isVisible.toggle}
                isVisible={isVisible.isTrue}
                uid={uid}
                selectedStepId={selectedStepId}
                relativeTime={relativeTime}
                isPlaybookQG={isPlaybookQG}
            />
        </>
    )
}
